import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { SectionBand } from "@/components/SectionPrimitives";
import { EDITORIAL_PHOTOS } from "@/lib/editorialPhotos";

// -----------------------------------------------------------------------
// Clients index - refonte 2026 (Citwell style).
// Cream hero + hero photo, then a wide editorial directory grouped by
// sector. Each row is a big-type card-like entry on cream background.
// -----------------------------------------------------------------------

const CAP_LABELS = {
  advisory: { en: "Advisory & Transformation", fr: "Advisory & Transformation" },
  delivery: { en: "Technology & Delivery",     fr: "Technologie & Delivery" },
  ai_build: { en: "AI & Decision Build",       fr: "IA & Decision Build" },
};

const groupBySector = (items, lang) => {
  const other = lang === "fr" ? "Autres secteurs" : "Other sectors";
  const groups = new Map();
  for (const c of items) {
    const key = (c.sector && String(c.sector).trim()) || other;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  const keys = [...groups.keys()].filter((k) => k !== other).sort((a, b) => a.localeCompare(b));
  if (groups.has(other)) keys.push(other);
  return keys.map((k) => [k, groups.get(k)]);
};

const ClientRow = ({ c, idx, lang }) => {
  const isAnonymous = c.confidentiality === "anonymous";
  const displayName = c.client_name || c.anonymous_descriptor || "Confidential client";
  const showLogo = !!c.logo_url && !isAnonymous;
  return (
    <Link
      to={`/clients/${c.slug || c.id}`}
      data-testid={`client-row-${idx}`}
      className="group grid grid-cols-[80px_1fr_auto] sm:grid-cols-[160px_minmax(0,1fr)_minmax(0,220px)_40px] items-center gap-5 sm:gap-10 py-8 hover:bg-white rounded-2xl -mx-4 px-4 transition-colors min-h-[100px]"
    >
      <div className="h-12 sm:h-14 flex items-center">
        {showLogo ? (
          <img
            src={c.logo_url}
            alt={displayName}
            width="160"
            height="56"
            className="max-h-10 sm:max-h-12 max-w-full object-contain object-left"
          />
        ) : (
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#9ca3af]">
            {isAnonymous ? (lang === "fr" ? "Confidentiel" : "Confidential") : displayName}
          </span>
        )}
      </div>

      <div className="min-w-0">
        <h3 className="font-display text-2xl sm:text-3xl text-[#0B1210] leading-[1.05] group-hover:text-[#00A878] transition-colors">
          {displayName}
        </h3>
        {c.mission_type && (
          <p className="mt-2 text-[13px] sm:text-[14px] font-bold-dl text-[#00A878]">
            {c.mission_type}
          </p>
        )}
      </div>

      <div className="hidden sm:flex flex-wrap gap-1.5 justify-end">
        {(c.capabilities || []).slice(0, 3).map((k) => (
          <span
            key={k}
            className="text-[11px] font-bold-dl text-[#4B5563] border border-[#E6E0D5] rounded-full px-3 py-1.5"
          >
            {CAP_LABELS[k]?.[lang] || k}
          </span>
        ))}
      </div>

      <ArrowRight
        size={20}
        strokeWidth={2.5}
        className="text-[#4B5563] group-hover:text-[#00A878] group-hover:translate-x-1 transition-all shrink-0 justify-self-end"
      />
    </Link>
  );
};

const Clients = () => {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/clients?language=${lang}&limit=60`)
      .then((r) => setItems((r.data || []).filter((c) => c.confidentiality === "anonymous" || process.env.REACT_APP_CLIENT_REFERENCES_APPROVED === "true")))
      .finally(() => setLoading(false));
  }, [lang]);

  const groups = useMemo(() => groupBySector(items, lang), [items, lang]);

  return (
    <div data-testid="clients-page">
      <SeoHead
        lang={lang}
        path="/clients"
        title={lang === "fr" ? "Clients & Références - Dataleo" : "Clients & References - Dataleo"}
        description={lang === "fr"
          ? "Références clients Dataleo : programmes Supply Chain, APS, Decision Apps et TMA - publics et anonymes."
          : "Dataleo client references: Supply Chain, APS, Decision Apps and application management programs - public and anonymous."}
      />
      <PageHero
        tone="cream"
        kicker={lang === "fr" ? "Clients & Références" : "Clients & References"}
        title={lang === "fr" ? "Transformations menées côté client." : "Transformations led client-side."}
        description={lang === "fr"
          ? "Dataleo intervient client-side sur des programmes Supply Chain complexes. Certains clients sont référencés publiquement, d'autres restent confidentiels."
          : "Dataleo operates client-side on complex Supply Chain programs. Some clients are publicly named, others remain confidential."}
        primaryCta={{ to: "/contact", label: lang === "fr" ? "Discuter d'un cas" : "Discuss a case" }}

        testId="clients-hero"
      />

      <SectionBand tone="cream" testId="clients-directory">
        {loading ? (
          <p className="text-[#9ca3af] text-[13px] font-mono-dl uppercase tracking-widest">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </p>
        ) : items.length === 0 ? (
          <div className="max-w-2xl bg-white rounded-[24px] border border-[#E6E0D5] p-10">
            <p className="text-[#4B5563] text-[15px] leading-relaxed">
              {lang === "fr"
                ? "Références en cours de publication. Nos programmes clients sont soit strictement confidentiels, soit référencés au cas par cas avec l'accord du client."
                : "References are being progressively published. Our client engagements are either strictly confidential or referenced case by case with the client's agreement."}
            </p>
            <Link to="/contact" className="mt-6 dl-pill dl-pill-primary inline-flex">
              {lang === "fr" ? "Nous parler d'un cas concret" : "Ask us about a concrete case"}
              <ArrowRight size={15} strokeWidth={2.5} />
            </Link>
          </div>
        ) : (
          <div className="space-y-20">
            {groups.map(([sector, list], gi) => (
              <section key={sector} data-testid={`clients-sector-${gi}`}>
                <div className="flex items-baseline gap-4 border-b-2 border-[#0B1210] pb-5 mb-2">
                  <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                    {lang === "fr" ? "Secteur" : "Sector"}
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl text-[#0B1210] leading-tight">
                    {sector}
                  </h2>
                  <span className="ml-auto font-mono-dl text-[12px] tracking-widest text-[#4B5563]">
                    {String(list.length).padStart(2, "0")}
                  </span>
                </div>
                <div className="divide-y divide-[#E6E0D5]">
                  {list.map((c, i) => (
                    <ClientRow key={c.id} c={c} idx={`${gi}-${i}`} lang={lang} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </SectionBand>
    </div>
  );
};

export default Clients;
